import { type AnyConstructor } from "../../../types/internal/utility";
import { WORKER_PORT_CALLBACK } from "./WorkerPort";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function WorkerObjectParser<TBase extends AnyConstructor>(Base: TBase) {
    return class WorkerObjectParser extends Base {
        public parseMessage(data: {
            type: string;
            message: any;
            success: null | string;
        }, callbacks: Record<string, Function>): {
            type: string;
            message: any;
            success: Function;
        } {
            const { type, message, success } = data;

            return {
                type,
                message: this.parseValue(message, callbacks),
                success: this.parseValue(success, callbacks),
            };
        }

        public parseValue(value: any, callbacks: Record<string, Function>): any {
            if(Array.isArray(value)) {
                // Parse array
                return this.parseArray(value, callbacks);
            } else if(typeof value === "object") {
                // Parse object
                return this.parseObject(value, callbacks);
            } else {
                // Add value to object
                return value;
            }
        }

        public parseObject(
            object: null | Record<string, any>,
            callbacks: Record<string, Function>
        ): null | Record<string, any> {
            if(object === null) {
                return object;
            }

            const results: Record<string, any> = {};

            // Iterate through object
            for(const key in object) {
                if(Object.prototype.hasOwnProperty.call(object, key)) {
                    const value = object[key];

                    // If object contains __callback assume it was originally a function
                    if(key === "__callback") {
                        // Prepare callback function
                        return this.parseCallback(value, callbacks);
                    } else {
                        // Parse value
                        results[key] = this.parseValue(value, callbacks);
                    }
                }
            }

            return results;
        }

        public parseArray(array: Array<any>, callbacks: Record<string, Function>): Array<any> {
            const results = [];

            // Loop through the array indexes
            for(let i = 0, l = array.length; i < l; i++) {
                // Parse item and add to results
                results.push(this.parseValue(array[i], callbacks));
            }

            return results;
        }

        public parseCallback(id: string, callbacks: Record<string, Function>): Function {
            // Add callback id to array for cleanup
            if(Array.isArray(callbacks)) {
                callbacks.push(id);
            }

            // Create callback function
            return (...args: Array<any>): void => {
                this.sendMessage(WORKER_PORT_CALLBACK, {
                    id  : id,
                    args: args,
                });
            };
        }

        public sendMessage(event: string, message: any): void {
            console.error("Received message but sendMessage has not been implemented", event, message);
            throw new TypeError("Send message function has not been implemented");
        }
    };
}
